import { useEffect, useRef, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import type { Entry } from '@ozone/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { entryHeadline } from '../compose';
import { DraftPreview } from './DraftPreview';
import { MbrPeriodPicker } from './MbrPeriodPicker';

/** Small uppercase column label matching the CategorySubsection heading style. */
function PaneLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
      {children}
    </span>
  );
}

interface DraftEditorProps {
  entry: Entry;
  onSaveDraft: (entryId: string, md: string) => Promise<void> | void;
  onSavePeriod?: (entryId: string, mbrPeriod: string) => Promise<void> | void;
  onRegenerate?: (entryId: string) => Promise<void> | void;
}

/** How long we wait before warning the admin to refresh a stuck regenerate. */
const REGEN_WATCHDOG_MS = 60000;

/**
 * AOZONE-32: inline editable MBR draft. Behavior is driven by
 * entry.mbrDraftStatus:
 *  - pending → "generating draft…" placeholder, textarea + Save disabled.
 *  - failed  → a ⚠️ warning, but the textarea is editable so an admin can
 *              hand-author a draft; Save is dirty-gated.
 *  - ready / undefined → editable textarea, Save dirty-gated.
 *
 * A controlled-reset effect adopts a fresh server draft only while the local
 * edits are clean — unsaved admin edits are never clobbered by a background
 * poll. A beforeunload guard nags on navigation while dirty.
 */
export function DraftEditor({
  entry,
  onSaveDraft,
  onSavePeriod = () => {},
  onRegenerate = () => {},
}: DraftEditorProps) {
  const initial = entry.mbrDraftMd ?? '';
  const initialPeriod = entry.mbrPeriod ?? '';
  const [draft, setDraft] = useState(initial);
  const [baseline, setBaseline] = useState(initial);
  const [period, setPeriod] = useState(initialPeriod);
  const [periodBaseline, setPeriodBaseline] = useState(initialPeriod);
  const [periodValid, setPeriodValid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmingRegen, setConfirmingRegen] = useState(false);
  const [regenTimedOut, setRegenTimedOut] = useState(false);
  // Bumped each time a regenerate is confirmed; arms a fresh 60s watchdog.
  const [regenAttempt, setRegenAttempt] = useState(0);

  const status = entry.mbrDraftStatus;
  const isPending = status === 'pending';
  const draftDirty = draft !== baseline;
  const periodDirty = period !== periodBaseline;
  const dirty = draftDirty || periodDirty;
  const headline = entryHeadline(entry);

  // baselineRef/draftRef mirror current state for the controlled-reset effect
  // so it can compare without re-running on every keystroke.
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Track the last server value we reconciled against. The effect only acts
  // when the *incoming* server draft genuinely changes — re-renders that don't
  // change the server value (e.g. an unrelated parent state update) must NOT
  // reset the editor, or they would swallow an in-flight keystroke.
  const lastIncomingRef = useRef(initial);

  // Controlled reset: when the server draft changes (e.g. a poll resolves the
  // pending draft), adopt it ONLY if the local editor is not dirty. Otherwise
  // keep the admin's unsaved edits and just track the new server baseline.
  useEffect(() => {
    const incoming = entry.mbrDraftMd ?? '';
    if (incoming === lastIncomingRef.current) return;
    lastIncomingRef.current = incoming;
    if (draftRef.current === baselineRef.current) {
      setBaseline(incoming);
      setDraft(incoming);
    } else {
      setBaseline(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.mbrDraftMd, entry.mbrDraftStatus]);

  // beforeunload guard — nag the admin if they try to leave with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Mirror the current pending-ness so the watchdog's deferred callback reads
  // the latest status (the timer is armed before the optimistic flip lands).
  const isPendingRef = useRef(isPending);
  isPendingRef.current = isPending;

  // AOZONE-35: 60s regenerate watchdog. Armed when the admin confirms a
  // regenerate (regenAttempt bumps). When the timer fires, warn the admin to
  // refresh ONLY if the entry is still 'pending'. If the draft came back
  // (status left 'pending') before 60s, the message never shows. The timer is
  // also torn down on unmount or a new attempt.
  useEffect(() => {
    if (regenAttempt === 0) return;
    const id = setTimeout(() => {
      if (isPendingRef.current) setRegenTimedOut(true);
    }, REGEN_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [regenAttempt]);

  // Clear a stale "timed out" warning once the draft actually comes back
  // (status leaves 'pending'), e.g. a slow regenerate that resolves post-60s.
  useEffect(() => {
    if (!isPending && regenTimedOut) setRegenTimedOut(false);
  }, [isPending, regenTimedOut]);

  const onConfirmRegen = () => {
    setConfirmingRegen(false);
    setRegenTimedOut(false);
    setRegenAttempt((n) => n + 1);
    // Fire-and-forget: the parent flips the entry to 'pending' optimistically
    // and the watchdog handles the slow case. Swallow rejection here so an
    // unhandled promise doesn't surface; the parent reverts on failure.
    void Promise.resolve(onRegenerate(entry.entryId)).catch(() => {});
  };

  const onSaveClick = async () => {
    setSaving(true);
    try {
      // Run draft + period saves in parallel; settle independently so one
      // failure doesn't mask the other's success (and the local baseline is
      // only updated for the leg that actually succeeded — keeps that field
      // dirty for retry).
      const results = await Promise.allSettled([
        draftDirty ? Promise.resolve(onSaveDraft(entry.entryId, draft)) : Promise.resolve(),
        periodDirty
          ? Promise.resolve(onSavePeriod(entry.entryId, period))
          : Promise.resolve(),
      ]);
      if (draftDirty && results[0].status === 'fulfilled') setBaseline(draft);
      if (periodDirty && results[1].status === 'fulfilled') setPeriodBaseline(period);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header: label + status badges on the left, Regenerate on the right. */}
      <div className="flex items-center justify-between">
        <div className="flex min-h-[1.375rem] flex-wrap items-center gap-2">
          <PaneLabel>MBR Draft</PaneLabel>
          {isPending && (
            <Badge variant="secondary" className="gap-1 text-slate-600">
              <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
              Generating draft…
            </Badge>
          )}
          {status === 'failed' && (
            <Badge variant="destructive">⚠️ Draft generation failed</Badge>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`Regenerate draft: ${headline}`}
          disabled={isPending}
          onClick={() => setConfirmingRegen(true)}
        >
          <RefreshCw aria-hidden />
          Regenerate
        </Button>
      </div>

      {/* AOZONE-59: mbrPeriod picker — admins can re-bucket an entry into
          a different MBR period (e.g. fix a late submission). */}
      <div className="flex flex-col gap-1">
        <PaneLabel>MBR Period</PaneLabel>
        <MbrPeriodPicker
          value={period}
          onChange={setPeriod}
          onValidityChange={setPeriodValid}
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-1">
          <PaneLabel>Markdown</PaneLabel>
          <Textarea
            className="min-h-[10rem] p-4 font-mono text-[13px] leading-relaxed"
            aria-label={`MBR draft: ${headline}`}
            placeholder="Write the MBR draft in Markdown…"
            value={draft}
            disabled={isPending}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <PaneLabel>Preview</PaneLabel>
          <div className="min-h-[10rem] rounded-md border border-slate-200 bg-muted/30 p-4">
            <DraftPreview md={draft} />
          </div>
        </div>
      </div>

      {status === 'failed' && (
        <p className="text-xs text-slate-500">
          You can author a draft manually below, or regenerate it.
        </p>
      )}
      {regenTimedOut && (
        <p className="text-xs font-medium text-amber-700">
          Regeneration is taking longer than expected — it may have timed out.
          Please refresh to check the latest draft.
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        {dirty && (
          <span className="text-xs font-medium text-amber-700">Unsaved changes</span>
        )}
        <Button
          type="button"
          size="sm"
          aria-label={`Save draft: ${headline}`}
          disabled={!dirty || saving || (periodDirty && !periodValid)}
          onClick={onSaveClick}
        >
          Save
        </Button>
      </div>

      {confirmingRegen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmingRegen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Regenerate MBR Draft"
            className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold leading-none tracking-tight text-slate-900">
              Regenerate MBR Draft?
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              This will overwrite the current draft for{' '}
              <span className="font-medium text-slate-700">{headline}</span> with a
              freshly AI-generated one. Any unsaved manual edits will be lost.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setConfirmingRegen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={onConfirmRegen}
              >
                <RefreshCw aria-hidden />
                Regenerate
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
